import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {Promise<void> | null} */
  #drawerRefreshPromise = null;

  /** Prevents the drawer-open lifecycle from fetching stale, pre-add content. */
  #cartUpdatePending = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('click', this.#handleRemoveClick, true);
    this.addEventListener('click', this.#handleSwitchClick);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('click', this.#handleRemoveClick, true);
    this.removeEventListener('click', this.#handleSwitchClick);
  }

  /**
   * "Switch to 3 pouches" buttons (snippets/ryedical-drawer-switch.liquid).
   * Replaces every Ryedical pouch line with one 3-pouch line, one-time or on
   * the subscription plan, then re-renders the drawer.
   * @param {MouseEvent} event
   */
  #handleSwitchClick = async (event) => {
    if (!(event.target instanceof Element)) return;
    const button = /** @type {HTMLButtonElement | null} */ (event.target.closest('[data-drawer-switch]'));
    if (!button || !this.contains(button)) return;

    const group = button.closest('[data-line-keys]');
    const keys = (group?.getAttribute('data-line-keys') ?? '').split(',').filter(Boolean);
    const sellingPlan = button.dataset.sellingPlanId;
    group?.querySelectorAll('button').forEach((b) => { b.disabled = true; });

    const deferredUpdate = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: keys.map((id) => ({ id, quantity: 0 })),
        promise: deferredUpdate.promise,
      })
    );

    try {
      const post = (url, body) =>
        fetch(url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        }).then(async (response) => {
          const json = await response.json();
          if (!response.ok || json.errors || json.status >= 400) throw new Error(json.description || json.message || 'Cart update failed');
          return json;
        });

      await post(`${Theme.routes.cart_update_url || '/cart/update'}.js`, { updates: Object.fromEntries(keys.map((key) => [key, 0])) });
      const item = { id: Number(button.dataset.variantId), quantity: 1 };
      if (sellingPlan) item.selling_plan = Number(sellingPlan);
      await post(Theme.routes.cart_add_url, { items: [item] });
      const cart = await fetch(`${Theme.routes.cart_url || '/cart'}.js`, { credentials: 'same-origin' }).then((r) => r.json());

      deferredUpdate.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'cart-drawer-component', didError: false },
      });

      await this.#refreshDrawerContent();
      this.#updateStickyState();
    } catch (error) {
      deferredUpdate.reject(error);
      group?.querySelectorAll('button').forEach((b) => { b.disabled = false; });
      console.error('[cart-drawer] Unable to switch to 3 pouches:', error);
      await this.#refreshDrawerContent();
    }
  };

  /**
   * Removes a drawer line without depending on a freshly injected
   * cart-items-component having completed its upgrade. This is especially
   * important on the PDP, where the empty drawer is replaced immediately
   * after add-to-cart and a shopper may click Remove straight away.
   * @param {MouseEvent} event
   */
  #handleRemoveClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const removeButton = /** @type {HTMLButtonElement | null} */ (event.target.closest('.cart-items__remove'));
    if (!removeButton || !this.contains(removeButton)) return;

    const lineMatch = removeButton.getAttribute('on:click')?.match(/\/onLineItemRemove\/(\d+)/);
    const row = removeButton.closest('[data-key]');
    const line = Number(lineMatch?.[1]);
    if (!Number.isInteger(line) || line < 1) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    removeButton.disabled = true;
    row?.setAttribute('aria-busy', 'true');

    const deferredUpdate = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: row?.getAttribute('data-key') ?? '', quantity: 0 }],
        promise: deferredUpdate.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ line, quantity: 0 }),
      });
      const cart = await response.json();
      if (!response.ok || cart.errors) throw new Error(cart.description || cart.message || 'Unable to remove item');

      deferredUpdate.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-drawer-component',
          didError: false,
        },
      });

      await this.#refreshDrawerContent();
      this.#updateStickyState();
    } catch (error) {
      deferredUpdate.reject(error);
      removeButton.disabled = false;
      row?.removeAttribute('aria-busy');
      console.error('[cart-drawer] Unable to remove cart item:', error);
    }
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = async () => {
    if (!this.#cartUpdatePending) await this.#refreshDrawerContent();
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen) {
      this.#cartUpdatePending = true;
      this.#setLoading(true);
    }

    if (shouldAutoOpen && !sourceModal) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(async ({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          if (!detail?.didError && !this.#renderDrawerContent(detail?.sections?.['cart-drawer-section'])) {
            await this.#refreshDrawerContent();
          }
          this.#cartUpdatePending = false;
          this.#setLoading(false);
          if (detail?.didError && shouldAutoOpen && this.#themeDrawer?.isOpen) this.#themeDrawer.close();
          settle();
          return;
        }

        if (!this.#renderDrawerContent(detail?.sections?.['cart-drawer-section'])) {
          await this.#refreshDrawerContent();
        }
        this.#cartUpdatePending = false;
        this.#setLoading(false);

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        this.#cartUpdatePending = false;
        this.#setLoading(false);
        if (shouldAutoOpen && this.#themeDrawer?.isOpen) this.#themeDrawer.close();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Shows immediate feedback while Shopify completes the cart mutation.
   * The drawer opens before the add resolves, so until then it still holds
   * the previous content: on a first add that is the empty-cart selector
   * ("Your cart is empty" + 2 pouches preselected). The CSS hides that
   * content and this real status element replaces it, so shoppers, screen
   * readers and SimGym agents never read the stale cart as the result.
   * @param {boolean} loading
   */
  #setLoading(loading) {
    const inner = this.querySelector('.cart-drawer__inner');
    if (!inner) return;
    let status = inner.querySelector(':scope > .cart-drawer__loading');
    if (loading) {
      inner.setAttribute('aria-busy', 'true');
      if (!status) {
        status = document.createElement('div');
        status.className = 'cart-drawer__loading';
        status.setAttribute('role', 'status');
        status.textContent = 'Adding to your cart…';
        inner.append(status);
      }
    } else {
      inner.removeAttribute('aria-busy');
      status?.remove();
    }
  }

  /**
   * Reuses the section HTML returned by `/cart/add.js`, avoiding another
   * network round trip before the drawer can show the added item.
   * @param {string | undefined} sectionHtml
   * @returns {boolean}
   */
  #renderDrawerContent(sectionHtml) {
    if (!sectionHtml) return false;

    const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextInner = documentFragment.querySelector('.cart-drawer__inner');
    const currentInner = this.querySelector('.cart-drawer__inner');
    if (!nextInner || !currentInner) return false;

    const sectionWrapper = this.closest('#shopify-section-cart-drawer-section');
    const nextStylesheet = documentFragment.querySelector('style[data-section-stylesheet]');
    if (sectionWrapper && nextStylesheet) {
      const currentStylesheet = sectionWrapper.querySelector('style[data-section-stylesheet]');
      if (currentStylesheet) {
        currentStylesheet.textContent = nextStylesheet.textContent;
      } else {
        sectionWrapper.prepend(document.importNode(nextStylesheet, true));
      }
    }

    currentInner.replaceWith(document.importNode(nextInner, true));
    return true;
  }

  /**
   * Re-renders the cart drawer from Shopify so an initially empty drawer cannot
   * remain stale after a successful add. This also covers header and View Cart opens.
   */
  async #refreshDrawerContent() {
    if (this.#drawerRefreshPromise) return this.#drawerRefreshPromise;

    this.#drawerRefreshPromise = (async () => {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', 'cart-drawer-section');
      url.searchParams.set('_ryedical_cart_refresh', Date.now().toString());

      const response = await fetch(url, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Failed to refresh cart drawer: ${response.status}`);

      if (!this.#renderDrawerContent(await response.text())) {
        throw new Error('Cart drawer content missing from refresh response');
      }
    })()
      .catch((error) => {
        console.warn('[cart-drawer] Unable to refresh drawer content:', error);
      })
      .finally(() => {
        this.#drawerRefreshPromise = null;
      });

    return this.#drawerRefreshPromise;
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
